import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const audienceDisplay = (row) => row.audience_display ?? (row.audience_type === 'all_users' ? 'All schools' : row.audience_type === 'specific_users' ? 'Specific users' : '—');

const canResendNotification = (row) => {
  if (!row) return false;
  if (row.notification_type === 'push') return true;
  if (row.notification_type === 'email') {
    return row.template_name === 'school_available' || row.payload?.kind === 'school_available';
  }
  return false;
};

const StaffNotifications = () => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [activeTab, setActiveTab] = useState('send');
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    audienceMode: 'schools',
    schoolIds: [],
    specificEmails: ''
  });
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [result, setResult] = useState(null);

  const [historyList, setHistoryList] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    date_range: '',
    notification_type: '',
    status: '',
    search: ''
  });
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [tableMessage, setTableMessage] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [previewCount, setPreviewCount] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [showConfirmAllModal, setShowConfirmAllModal] = useState(false);

  const [availabilityQueues, setAvailabilityQueues] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState(null);
  const [sendingSchoolId, setSendingSchoolId] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        setAuthChecked(true);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login/');
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    const fetchData = async () => {
      try {
        const schoolsData = await apiService.getSchools();
        const schoolsList = schoolsData.results || schoolsData;
        setSchools(schoolsList);
      } catch (error) {
        console.error('Failed to fetch schools:', error);
      } finally {
        setLoadingSchools(false);
      }
    };
    fetchData();
  }, [authChecked]);

  const fetchHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (historyFilters.date_range) params.date_range = historyFilters.date_range;
      if (historyFilters.status) params.status = historyFilters.status;
      if (historyFilters.search) params.search = historyFilters.search;
      const data = await apiService.getNotificationHistory(params);
      setHistoryList(data.results || []);
      setHistoryCount(data.count || 0);
      setHistoryPage(page);
    } catch (error) {
      console.error('Failed to fetch notification history:', error);
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilters.date_range, historyFilters.status, historyFilters.search]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await apiService.getNotificationHistoryStats({ days: 14 });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch notification stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authChecked && activeTab === 'history') {
      fetchHistory(1);
      fetchStats();
    }
  }, [authChecked, activeTab, fetchHistory, fetchStats]);

  const fetchAvailabilityQueues = useCallback(async () => {
    setAvailabilityLoading(true);
    setAvailabilityMessage(null);
    try {
      const data = await apiService.getSchoolAvailabilityEmailQueues();
      setAvailabilityQueues(data.results || []);
    } catch (error) {
      console.error('Failed to fetch school availability email queues:', error);
      setAvailabilityQueues([]);
      setAvailabilityMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to load pending school emails.',
      });
    } finally {
      setAvailabilityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authChecked && activeTab === 'school-emails') {
      fetchAvailabilityQueues();
    }
  }, [authChecked, activeTab, fetchAvailabilityQueues]);

  const handleSendSchoolAvailabilityEmails = async (schoolId) => {
    setSendingSchoolId(schoolId);
    setAvailabilityMessage(null);
    try {
      const data = await apiService.sendSchoolAvailabilityEmails(schoolId);
      setAvailabilityMessage({
        type: 'success',
        text: data.message || `Sent ${data.sent_count} email(s).`,
      });
      await fetchAvailabilityQueues();
    } catch (error) {
      setAvailabilityMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to send school emails.',
      });
    } finally {
      setSendingSchoolId(null);
    }
  };

  const fetchPreview = useCallback(async () => {
    const isAll = formData.audienceMode === 'all';
    const hasSchools = formData.audienceMode === 'schools' && formData.schoolIds.length > 0;
    const hasEmails = formData.audienceMode === 'emails' && (formData.specificEmails || '').trim();
    if (!isAll && !hasSchools && !hasEmails) {
      setPreviewRecipients([]);
      setPreviewCount(0);
      setPreviewError(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const payload = {};
      if (formData.audienceMode === 'emails') {
        payload.emails = (formData.specificEmails || '').split(/[\n,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
      } else {
        payload.school_ids = formData.audienceMode === 'all' ? [] : formData.schoolIds;
      }
      const data = await apiService.getNotificationPreview(payload, { page: 1, page_size: 100 });
      setPreviewRecipients(data.results || []);
      setPreviewCount(data.count ?? 0);
    } catch (err) {
      setPreviewRecipients([]);
      setPreviewCount(0);
      setPreviewError(err.response?.data?.error || err.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [formData.audienceMode, formData.schoolIds, formData.specificEmails]);

  useEffect(() => {
    if (!authChecked || activeTab !== 'send') return;
    const isAll = formData.audienceMode === 'all';
    const hasSchools = formData.audienceMode === 'schools' && formData.schoolIds.length > 0;
    const hasEmails = formData.audienceMode === 'emails' && (formData.specificEmails || '').trim();
    if (!isAll && !hasSchools && !hasEmails) {
      setPreviewRecipients([]);
      setPreviewCount(0);
      setPreviewError(null);
      return;
    }
    const t = setTimeout(fetchPreview, 400);
    return () => clearTimeout(t);
  }, [authChecked, activeTab, formData.audienceMode, formData.schoolIds, formData.specificEmails, fetchPreview]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetailItem(null);
    setResendMessage(null);
    try {
      const item = await apiService.getNotificationDetail(id);
      setDetailItem(item);
    } catch (error) {
      console.error('Failed to fetch notification detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResend = async () => {
    if (!detailItem?.id) return;
    setResendLoading(true);
    setResendMessage(null);
    try {
      const data = await apiService.resendNotification(detailItem.id);
      setResendMessage({ type: 'success', text: data.message || 'Notification resent successfully.' });
      fetchHistory(historyPage);
      fetchStats();
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Failed to resend notification';
      setResendMessage({ type: 'error', text: msg });
    } finally {
      setResendLoading(false);
    }
  };

  const handleResendFromRow = async (e, row) => {
    e.stopPropagation();
    if (!row?.id || !canResendNotification(row)) return;
    setResendingId(row.id);
    setTableMessage(null);
    try {
      const data = await apiService.resendNotification(row.id);
      setTableMessage({ type: 'success', text: data.message || 'Notification resent successfully.' });
      fetchHistory(historyPage);
      fetchStats();
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Failed to resend notification';
      setTableMessage({ type: 'error', text: msg });
    } finally {
      setResendingId(null);
    }
  };

  const applyHistoryFilters = () => {
    fetchHistory(1);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleSchoolToggle = (schoolId) => {
    setFormData(prev => {
      const schoolIds = prev.schoolIds.includes(schoolId)
        ? prev.schoolIds.filter(id => id !== schoolId)
        : [...prev.schoolIds, schoolId];
      return { ...prev, schoolIds };
    });
    setMessage({ type: '', text: '' });
  };

  const setAudienceMode = (mode) => {
    setFormData(prev => ({
      ...prev,
      audienceMode: mode,
      schoolIds: mode === 'schools' ? prev.schoolIds : [],
      specificEmails: mode === 'emails' ? prev.specificEmails : ''
    }));
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.audienceMode === 'emails') {
      const emails = (formData.specificEmails || '')
        .split(/[\n,;]+/)
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
      if (emails.length === 0) {
        setMessage({ type: 'error', text: 'Enter at least one email address.' });
        return;
      }
      setLoading(true);
      setMessage({ type: '', text: '' });
      setResult(null);
      try {
        const response = await apiService.sendMassNotification(
          formData.title,
          formData.body,
          {},
          [],
          emails
        );
        setResult(response.result);
        setMessage({ type: 'success', text: response.message || 'Notification sent successfully!' });
        setFormData(prev => ({ ...prev, title: '', body: '', specificEmails: '' }));
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to send notification';
        setMessage({ type: 'error', text: errorMessage });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (formData.audienceMode === 'schools' && formData.schoolIds.length === 0) {
      setMessage({
        type: 'error',
        text: 'Select at least one school.'
      });
      return;
    }

    if (formData.audienceMode === 'all') {
      setShowConfirmAllModal(true);
      return;
    }

    doSendToSchools(formData.schoolIds);
  };

  const doSendToSchools = async (schoolIds) => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    setResult(null);
    try {
      const response = await apiService.sendMassNotification(
        formData.title,
        formData.body,
        {},
        schoolIds
      );
      setResult(response.result);
      setMessage({
        type: 'success',
        text: response.message || 'Notification sent successfully!'
      });
      setFormData(prev => ({
        ...prev,
        title: '',
        body: ''
      }));
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send notification';
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSendAllSchools = async () => {
    setShowConfirmAllModal(false);
    await doSendToSchools(['all']);
  };

  if (!authChecked) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <div className="staff-page-header-row">
            <div>
              <h1 className="staff-page-header">Send Notifications</h1>
              <p className="staff-page-subheader">Send mass push notifications and view notification history.</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', borderBottom: '1px solid #e9ecef' }}>
            <button
              type="button"
              onClick={() => setActiveTab('send')}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: 'none',
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                cursor: 'pointer',
                borderBottom: activeTab === 'send' ? '2px solid #00C8FF' : '2px solid transparent',
                marginBottom: '-1px',
                fontWeight: activeTab === 'send' ? 600 : 400
              }}
            >
              Send Notification
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: 'none',
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                cursor: 'pointer',
                borderBottom: activeTab === 'history' ? '2px solid #00C8FF' : '2px solid transparent',
                marginBottom: '-1px',
                fontWeight: activeTab === 'history' ? 600 : 400
              }}
            >
              Notification History
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('school-emails')}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: 'none',
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                cursor: 'pointer',
                borderBottom: activeTab === 'school-emails' ? '2px solid #00C8FF' : '2px solid transparent',
                marginBottom: '-1px',
                fontWeight: activeTab === 'school-emails' ? 600 : 400
              }}
            >
              School Available Emails
            </button>
          </div>

          {activeTab === 'send' && (
          <div className="add-reward-section" style={{ marginTop: '24px' }}>
            <h2 className="staff-page-header" style={{ fontSize: '24px', marginBottom: '8px' }}>Send Push Notification</h2>
            <p className="staff-page-subheader" style={{ marginBottom: '24px' }}>
              Choose audience: all schools, selected school(s), or specific users by email. Users with push enabled will receive the message.
            </p>

            {loadingSchools ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>Loading schools...</div>
            ) : (
            <form onSubmit={handleSubmit} className="reward-form">
              <div className="form-group">
                <label className="form-label">Audience</label>
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', padding: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="audienceMode"
                        checked={formData.audienceMode === 'all'}
                        onChange={() => setAudienceMode('all')}
                        style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 500 }}>All schools</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', padding: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="audienceMode"
                        checked={formData.audienceMode === 'schools'}
                        onChange={() => setAudienceMode('schools')}
                        style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 500 }}>Selected school(s)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', padding: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="audienceMode"
                        checked={formData.audienceMode === 'emails'}
                        onChange={() => setAudienceMode('emails')}
                        style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 500 }}>Users by email (search)</span>
                    </label>
                  </div>

                  {formData.audienceMode === 'emails' && (
                    <div style={{ marginTop: '8px', marginBottom: '12px' }}>
                      <textarea
                        placeholder="Enter emails, one per line or comma-separated"
                        value={formData.specificEmails}
                        onChange={(e) => setFormData(prev => ({ ...prev, specificEmails: e.target.value }))}
                        className="form-textarea"
                        rows={4}
                        style={{ width: '100%', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}

                  {formData.audienceMode === 'schools' && (
                    <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '8px' }}>
                      {schools.map((school) => (
                        <label
                          key={school.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            marginBottom: '4px',
                            backgroundColor: formData.schoolIds.includes(school.id) ? '#e7f3ff' : 'transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.schoolIds.includes(school.id)}
                            onChange={() => handleSchoolToggle(school.id)}
                            style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span>
                            {school.name} {school.city && school.state ? `(${school.city}, ${school.state})` : ''}
                          </span>
                        </label>
                      ))}
                      {schools.length === 0 && (
                        <div style={{ padding: '12px', color: '#666', textAlign: 'center' }}>No schools available</div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {formData.audienceMode === 'emails'
                    ? `Sending to ${(formData.specificEmails || '').split(/[\n,;]+/).filter(s => s.trim()).length} email(s)`
                    : formData.audienceMode === 'all'
                      ? `Selected: All schools (${schools.length} schools)`
                      : `Selected: ${formData.schoolIds.length} school(s)`
                  }
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notification Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter notification title"
                  required
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Notification Message</label>
                <textarea
                  name="body"
                  value={formData.body}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Enter notification message..."
                  rows="6"
                  required
                  maxLength={500}
                />
                <div style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  marginTop: '4px',
                  textAlign: 'right'
                }}>
                  {formData.body.length}/500
                </div>
              </div>

              {message.text && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                  color: message.type === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {message.text}
                </div>
              )}

              {result && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  backgroundColor: '#e7f3ff',
                  border: '1px solid #b3d9ff'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Notification Results:</div>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    <div>✓ Successfully sent: {result.success} devices</div>
                    {result.failure > 0 && <div>✗ Failed: {result.failure} devices</div>}
                    <div style={{ marginTop: '8px', opacity: 0.7 }}>
                      Schools: {result.schools_count || 0} | Total users: {result.total_users || 0} | Total devices: {result.total_devices || 0}
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="submit"
                  className="staff-page-button"
                  disabled={
                    loading ||
                    !formData.title.trim() ||
                    !formData.body.trim() ||
                    (formData.audienceMode === 'schools' && formData.schoolIds.length === 0) ||
                    (formData.audienceMode === 'emails' && !formData.specificEmails.trim())
                  }
                  style={{
                    opacity: (loading || !formData.title.trim() || !formData.body.trim() || (formData.audienceMode === 'schools' && formData.schoolIds.length === 0) || (formData.audienceMode === 'emails' && !formData.specificEmails.trim())) ? 0.5 : 1,
                    cursor: (loading || !formData.title.trim() || !formData.body.trim() || (formData.audienceMode === 'schools' && formData.schoolIds.length === 0) || (formData.audienceMode === 'emails' && !formData.specificEmails.trim())) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span className="staff-page-button-text">
                    {loading ? 'Sending...' : 'Send Notification'}
                  </span>
                </button>
                {formData.title || formData.body ? (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        title: '',
                        body: ''
                      }));
                      setMessage({ type: '', text: '' });
                      setResult(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px'
                    }}
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              {(formData.audienceMode === 'all' || (formData.audienceMode === 'schools' && formData.schoolIds.length > 0) || (formData.audienceMode === 'emails' && (formData.specificEmails || '').trim())) && (
                <div style={{ marginTop: '24px', border: '1px solid #e9ecef', borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', background: '#f8f9fa', fontWeight: 600, fontSize: '14px' }}>
                    Who the notification would go to
                  </div>
                  {previewLoading ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>Loading…</div>
                  ) : previewError ? (
                    <div style={{ padding: '16px', color: '#991b1b', fontSize: '14px' }}>{previewError}</div>
                  ) : (
                    <>
                      <div style={{ padding: '8px 16px', fontSize: '12px', color: '#666' }}>
                        {previewCount} recipient{previewCount !== 1 ? 's' : ''}
                        {previewCount > previewRecipients.length && ` (showing first ${previewRecipients.length})`}
                      </div>
                      <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e9ecef', background: '#fafafa' }}>
                              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>ID</th>
                              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Full name</th>
                              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>School</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewRecipients.length === 0 ? (
                              <tr><td colSpan={3} style={{ padding: '16px', color: '#666', textAlign: 'center' }}>No recipients with push enabled for this audience.</td></tr>
                            ) : (
                              previewRecipients.map((r, i) => (
                                <tr key={r.user_id || i} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '10px 16px', fontFamily: 'monospace', fontSize: '12px' }}>{r.user_id || '—'}</td>
                                  <td style={{ padding: '10px 16px' }}>{r.full_name || '—'}</td>
                                  <td style={{ padding: '10px 16px' }}>{r.school_name || '—'}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </form>
            )}
          </div>
          )}

          {activeTab === 'history' && (
          <div className="add-reward-section" style={{ marginTop: '24px' }}>
            <h2 className="staff-page-header" style={{ fontSize: '24px', marginBottom: '16px' }}>Notification History</h2>

            {stats && (
              <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={{ padding: '12px 20px', background: '#f0f9ff', borderRadius: '8px', minWidth: '120px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Sent (14d)</div>
                  <div style={{ fontSize: '20px', fontWeight: 700 }}>{stats.total?.total_sent ?? 0}</div>
                </div>
                <div style={{ padding: '12px 20px', background: '#ecfdf5', borderRadius: '8px', minWidth: '120px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Delivered</div>
                  <div style={{ fontSize: '20px', fontWeight: 700 }}>{stats.total?.total_success ?? 0}</div>
                </div>
                <div style={{ padding: '12px 20px', background: '#fef2f2', borderRadius: '8px', minWidth: '120px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Failed</div>
                  <div style={{ fontSize: '20px', fontWeight: 700 }}>{stats.total?.total_failure ?? 0}</div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={historyFilters.date_range}
                onChange={(e) => setHistoryFilters(f => ({ ...f, date_range: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd' }}
              >
                <option value="">All dates</option>
                <option value="today">Today</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
              <select
                value={historyFilters.status}
                onChange={(e) => setHistoryFilters(f => ({ ...f, status: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd' }}
              >
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="partially_sent">Partially sent</option>
              </select>
              <input
                type="text"
                placeholder="Search title or body..."
                value={historyFilters.search}
                onChange={(e) => setHistoryFilters(f => ({ ...f, search: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', minWidth: '200px' }}
              />
              <button type="button" onClick={applyHistoryFilters} className="staff-page-button" style={{ padding: '8px 16px' }}>
                Apply
              </button>
              <button
                type="button"
                onClick={() => { fetchHistory(historyPage); fetchStats(); }}
                disabled={historyLoading}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  cursor: historyLoading ? 'not-allowed' : 'pointer',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  opacity: historyLoading ? 0.7 : 1
                }}
              >
                Reload
              </button>
            </div>

            {tableMessage && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                backgroundColor: tableMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                color: tableMessage.type === 'success' ? '#155724' : '#721c24',
                border: `1px solid ${tableMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                {tableMessage.text}
              </div>
            )}
            {historyLoading ? (
              <div style={{ padding: '24px', textAlign: 'center' }}>Loading...</div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e9ecef', textAlign: 'left' }}>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Date & time</th>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Title</th>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Audience</th>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Status</th>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Recipients</th>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Success / Fail</th>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Opens</th>
                        <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyList.length === 0 ? (
                        <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No notifications found.</td></tr>
                      ) : (
                        historyList.map((row) => (
                          <tr
                            key={row.id}
                            style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                            onClick={() => openDetail(row.id)}
                          >
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>{formatDateTime(row.sent_at)}</td>
                            <td style={{ padding: '12px 8px', fontSize: '14px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.title}>{row.title}</td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>{audienceDisplay(row)}</td>
                            <td style={{ padding: '12px 8px' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                background: row.status === 'sent' ? '#d1fae5' : row.status === 'failed' ? '#fee2e2' : '#fef3c7',
                                color: row.status === 'sent' ? '#065f46' : row.status === 'failed' ? '#991b1b' : '#92400e'
                              }}>
                                {row.status}
                              </span>
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>{row.recipient_count ?? 0}</td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>{row.success_count ?? 0} / {row.failure_count ?? 0}</td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>{row.opened_count_computed ?? row.opened_count ?? '—'}</td>
                            <td style={{ padding: '12px 8px' }} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button type="button" onClick={(e) => { e.stopPropagation(); openDetail(row.id); }} className="staff-page-button" style={{ padding: '6px 12px', fontSize: '12px' }}>View</button>
                                {canResendNotification(row) && (
                                  <button
                                    type="button"
                                    onClick={(e) => handleResendFromRow(e, row)}
                                    disabled={resendingId === row.id}
                                    className="staff-page-button"
                                    style={{ padding: '6px 12px', fontSize: '12px', opacity: resendingId === row.id ? 0.7 : 1 }}
                                  >
                                    {resendingId === row.id ? 'Sending...' : 'Resend'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {historyCount > 20 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>Total: {historyCount}</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" disabled={historyPage <= 1} onClick={() => fetchHistory(historyPage - 1)} className="staff-page-button" style={{ padding: '6px 12px', opacity: historyPage <= 1 ? 0.5 : 1 }}>Previous</button>
                      <button type="button" disabled={historyPage * 20 >= historyCount} onClick={() => fetchHistory(historyPage + 1)} className="staff-page-button" style={{ padding: '6px 12px', opacity: historyPage * 20 >= historyCount ? 0.5 : 1 }}>Next</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          )}

          {activeTab === 'school-emails' && (
          <div className="add-reward-section" style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <div>
                <h2 className="staff-page-header" style={{ fontSize: '24px', marginBottom: '8px' }}>School Available Emails</h2>
                <p className="staff-page-subheader" style={{ marginBottom: 0 }}>
                  Waitlist emails are not sent when you activate a school. Send them manually here, per school.
                </p>
              </div>
              <button
                type="button"
                onClick={fetchAvailabilityQueues}
                disabled={availabilityLoading}
                className="staff-page-button"
                style={{ padding: '10px 16px', opacity: availabilityLoading ? 0.7 : 1 }}
              >
                {availabilityLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {availabilityMessage && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  background: availabilityMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
                  color: availabilityMessage.type === 'success' ? '#065f46' : '#991b1b',
                }}
              >
                {availabilityMessage.text}
              </div>
            )}

            {availabilityLoading ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>Loading pending queues...</div>
            ) : (
              <div style={{ overflowX: 'auto', border: '1px solid #e9ecef', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>School</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Pending</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Already sent</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availabilityQueues.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                          No schools with pending waitlist emails.
                        </td>
                      </tr>
                    ) : (
                      availabilityQueues.map((school) => (
                        <tr key={school.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px 16px' }}>{school.name}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              fontSize: '12px',
                              background: school.is_active ? '#ecfdf5' : '#f3f4f6',
                              color: school.is_active ? '#065f46' : '#4b5563',
                            }}>
                              {school.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>{school.pending_count}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>{school.sent_count}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => handleSendSchoolAvailabilityEmails(school.id)}
                              disabled={sendingSchoolId === school.id || school.pending_count === 0}
                              className="staff-page-button"
                              style={{
                                padding: '8px 14px',
                                opacity: (sendingSchoolId === school.id || school.pending_count === 0) ? 0.6 : 1,
                                cursor: (sendingSchoolId === school.id || school.pending_count === 0) ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {sendingSchoolId === school.id
                                ? 'Sending...'
                                : `Send ${school.pending_count} email${school.pending_count === 1 ? '' : 's'}`}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {showConfirmAllModal && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '24px'
              }}
              onClick={() => setShowConfirmAllModal(false)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  maxWidth: '400px',
                  width: '100%',
                  padding: '24px'
                }}
                onClick={(ev) => ev.stopPropagation()}
              >
                <div style={{ fontWeight: 600, fontSize: '18px', marginBottom: '12px' }}>Are you sure?</div>
                <p style={{ margin: '0 0 20px', color: '#555', fontSize: '15px' }}>
                  This will send the notification to <strong>all schools</strong>. Every user with push enabled in any active school will receive it.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowConfirmAllModal(false)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      background: '#fff',
                      cursor: 'pointer',
                      fontFamily: '"Baikal Book"',
                      fontSize: '14px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmSendAllSchools}
                    disabled={loading}
                    className="staff-page-button"
                    style={{ padding: '10px 20px', opacity: loading ? 0.7 : 1 }}
                  >
                    {loading ? 'Sending...' : 'Yes, send to all schools'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {detailItem !== null && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '24px'
              }}
              onClick={() => setDetailItem(null)}
            >
              <div
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  maxWidth: '600px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  padding: '24px'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {detailLoading ? (
                  <div style={{ padding: '24px', textAlign: 'center' }}>Loading...</div>
                ) : detailItem ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <h3 style={{ margin: 0, fontSize: '20px' }}>{detailItem.title}</h3>
                      <button type="button" onClick={() => setDetailItem(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>×</button>
                    </div>
                    <p style={{ margin: '0 0 16px', color: '#666', fontSize: '14px' }}>{detailItem.body}</p>
                    <dl style={{ margin: 0, fontSize: '14px' }}>
                      <dt style={{ fontWeight: 600, marginTop: '12px' }}>Sent at</dt>
                      <dd style={{ margin: '4px 0 0' }}>{formatDateTime(detailItem.sent_at)}</dd>
                      <dt style={{ fontWeight: 600, marginTop: '12px' }}>Audience</dt>
                      <dd style={{ margin: '4px 0 0' }}>{detailItem.audience_display ?? audienceDisplay(detailItem)}</dd>
                      <dt style={{ fontWeight: 600, marginTop: '12px' }}>Status</dt>
                      <dd style={{ margin: '4px 0 0' }}>{detailItem.status} — {detailItem.success_count} delivered, {detailItem.failure_count} failed</dd>
                      {(detailItem.opened_count_computed != null || detailItem.opened_count != null) && (
                        <>
                          <dt style={{ fontWeight: 600, marginTop: '12px' }}>Engagement</dt>
                          <dd style={{ margin: '4px 0 0' }}>
                            Opened: {detailItem.opened_count_computed ?? detailItem.opened_count ?? 0} (unique)
                          </dd>
                        </>
                      )}
                      {detailItem.platform_split && Object.keys(detailItem.platform_split).length > 0 && (
                        <>
                          <dt style={{ fontWeight: 600, marginTop: '12px' }}>Platform split</dt>
                          <dd style={{ margin: '4px 0 0' }}>{JSON.stringify(detailItem.platform_split)}</dd>
                        </>
                      )}
                      {detailItem.payload && Object.keys(detailItem.payload).length > 0 && (
                        <>
                          <dt style={{ fontWeight: 600, marginTop: '12px' }}>Payload</dt>
                          <dd style={{ margin: '4px 0 0', wordBreak: 'break-all' }}><pre style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', overflow: 'auto', fontSize: '12px' }}>{JSON.stringify(detailItem.payload, null, 2)}</pre></dd>
                        </>
                      )}
                      {(detailItem.recipients_preview && (detailItem.recipients_preview.length > 0 || (detailItem.recipients_total ?? 0) > 0)) && (
                        <>
                          <dt style={{ fontWeight: 600, marginTop: '12px' }}>Sent to</dt>
                          <dd style={{ margin: '4px 0 0' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                              {detailItem.recipients_total ?? 0} recipient{(detailItem.recipients_total ?? 0) !== 1 ? 's' : ''}
                              {detailItem.recipients_truncated && ' (showing first 100)'}
                            </div>
                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid #eee', background: '#f8f9fa' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>ID</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Full name</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>School</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(detailItem.recipients_preview || []).map((r, i) => (
                                    <tr key={r.user_id || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px' }}>{r.user_id || '—'}</td>
                                      <td style={{ padding: '8px 12px' }}>{r.full_name || '—'}</td>
                                      <td style={{ padding: '8px 12px' }}>{r.school_name || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </dd>
                        </>
                      )}
                      {(detailItem.delivered_recipients?.length > 0 || detailItem.failed_recipients?.length > 0) && (
                        <>
                          {detailItem.delivered_recipients?.length > 0 && (() => {
                            const unique = [...new Map((detailItem.delivered_recipients || []).map(r => [r.email || r.user_id, r])).values()];
                            return (
                              <>
                                <dt style={{ fontWeight: 600, marginTop: '12px', color: '#065f46' }}>Delivered</dt>
                                <dd style={{ margin: '4px 0 0' }}>
                                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#065f46', maxHeight: '120px', overflowY: 'auto' }}>
                                    {unique.slice(0, 50).map((r, i) => (
                                      <li key={i}>{r.email || r.user_id}</li>
                                    ))}
                                    {unique.length > 50 && <li style={{ color: '#666' }}>… and {unique.length - 50} more</li>}
                                  </ul>
                                </dd>
                              </>
                            );
                          })()}
                          {detailItem.failed_recipients?.length > 0 && (() => {
                            const unique = [...new Map((detailItem.failed_recipients || []).map(r => [r.email || r.user_id, r])).values()];
                            return (
                              <>
                                <dt style={{ fontWeight: 600, marginTop: '12px', color: '#991b1b' }}>Failed / Skipped</dt>
                                <dd style={{ margin: '4px 0 0' }}>
                                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#991b1b', maxHeight: '120px', overflowY: 'auto' }}>
                                    {unique.slice(0, 50).map((r, i) => (
                                      <li key={i}>{r.email || r.user_id}</li>
                                    ))}
                                    {unique.length > 50 && <li style={{ color: '#666' }}>… and {unique.length - 50} more</li>}
                                  </ul>
                                </dd>
                              </>
                            );
                          })()}
                        </>
                      )}
                      {(!detailItem.delivered_recipients?.length && !detailItem.failed_recipients?.length) && detailItem.recipient_sample?.length > 0 && (
                        <>
                          <dt style={{ fontWeight: 600, marginTop: '12px' }}>Recipients (targeted)</dt>
                          <dd style={{ margin: '4px 0 0' }}>
                            <ul style={{ margin: 0, paddingLeft: '20px' }}>
                              {detailItem.recipient_sample.slice(0, 20).map((r, i) => (
                                <li key={i}>{r.email || r.user_id}</li>
                              ))}
                              {detailItem.recipient_sample.length > 20 && <li style={{ color: '#666' }}>… and {detailItem.recipient_sample.length - 20} more</li>}
                            </ul>
                            <span style={{ fontSize: '12px', color: '#666' }}>Delivery status not recorded for this send.</span>
                          </dd>
                        </>
                      )}
                      {detailItem.errors && detailItem.errors.length > 0 && (
                        <>
                          <dt style={{ fontWeight: 600, marginTop: '12px', color: '#991b1b' }}>Errors</dt>
                          <dd style={{ margin: '4px 0 0' }}>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#991b1b' }}>
                              {detailItem.errors.map((err, i) => (
                                <li key={i}>{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                              ))}
                            </ul>
                          </dd>
                        </>
                      )}
                    </dl>
                    {canResendNotification(detailItem) && (
                      <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={handleResend}
                          disabled={resendLoading}
                          className="staff-page-button"
                          style={{ opacity: resendLoading ? 0.7 : 1 }}
                        >
                          {resendLoading ? 'Sending...' : 'Resend'}
                        </button>
                        {resendMessage && (
                          <span style={{
                            fontSize: '14px',
                            color: resendMessage.type === 'success' ? '#065f46' : '#991b1b'
                          }}>
                            {resendMessage.text}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffNotifications;

